/*
 * Copyright (c) 2016-2017 VMware, Inc. All Rights Reserved.
 * This software is released under MIT license.
 * The full license information can be found in LICENSE in the root directory of this project.
 */

import {Component, ContentChild, ElementRef, EventEmitter, HostBinding, Input, Output, ViewChild} from '@angular/core';
import {Subscription} from 'rxjs/Subscription';

import {DatagridPropertyComparator} from './built-in/comparators/datagrid-property-comparator';
import {DatagridPropertyStringFilter} from './built-in/filters/datagrid-property-string-filter';
import {DatagridStringFilterImpl} from './built-in/filters/datagrid-string-filter-impl';
import {DatagridHideableColumn} from './datagrid-hideable-column';
import {Comparator} from './interfaces/comparator';
import {SortOrder} from './interfaces/sort-order';
import {CustomFilter} from './providers/custom-filter';
import {DragDispatcher} from './providers/drag-dispatcher';
import {FiltersProvider} from './providers/filters';
import {Sort} from './providers/sort';
import {DatagridFilterRegistrar} from './utils/datagrid-filter-registrar';

let /** @type {?} */ nbCount: number = 0;
export class DatagridColumn extends DatagridFilterRegistrar<DatagridStringFilterImpl> {
/**
 * @param {?} _sort
 * @param {?} filters
 * @param {?} _dragDispatcher
 */
constructor(private _sort: Sort, filters: FiltersProvider,
private _dragDispatcher: DragDispatcher) {
        super(filters);
        this._sortSubscription = _sort.change.subscribe(sort => {
            // We're only listening to make sure we emit an event when the column goes from sorted to unsorted
            if (this.sortOrder !== SortOrder.Unsorted && sort.comparator !== this._sortBy) {
                this._sortOrder = SortOrder.Unsorted;
                this.sortOrderChange.emit(this._sortOrder);
            }
            // deprecated: to be removed - START
            if (this.sorted && sort.comparator !== this._sortBy) {
                this._sorted = false;
                this.sortedChange.emit(false);
            }
            // deprecated: to be removed - END
        });

        this.columnId = "dg-col-" + nbCount.toString();  // Approximate a GUID
        nbCount++;
        // put index here
    }
/**
 * \@property columnId
 * 
 * \@description 
 * A DatagridColumn class variable that holds the number of DatagridColumn instances for a Datagrid.
 * It is used to generate a unique id for the DatagridColumn instance.
 * 
 */
public columnId: string;
/**
 * \@property hidden
 * 
 * \@description 
 * A property that allows the column to be hidden / shown with css
 * Note the default allows the DatagridColumn to have an *ngIf on it. (EHCAIWC - will occur if its not initialized)
 * 
 * \@default false
 * 
 * @return {?}
 */
public get hidden(): boolean {
        return !!this.hideable && this.hideable.hidden;
    }
/**
 * @param {?} value
 * @return {?}
 */
set handleElRef(value: ElementRef) {
        this._dragDispatcher.handleRef = value;
    }
/**
 * @param {?} value
 * @return {?}
 */
set handleTrackerElRef(value: ElementRef) {
        this._dragDispatcher.handleTrackerRef = value;
    }
/**
 * Subscription to the sort service changes
 */
private _sortSubscription: Subscription;
/**
 * @return {?}
 */
ngOnDestroy() {
        this._sortSubscription.unsubscribe();
    }
private _field: string;
/**
 * @return {?}
 */
public get field() {
        return this._field;
    }
/**
 * @param {?} field
 * @return {?}
 */
public set field(field: string) {
        if (typeof field === "string") {
            this._field = field;
            if (!this.customFilter) {
                this.setFilter(new DatagridStringFilterImpl(new DatagridPropertyStringFilter(field)));
            }
            if (!this._sortBy) {
                this._sortBy = new DatagridPropertyComparator(field);
            }
        }
    }
/**
 * Comparator to use when sorting the column
 */
private _sortBy: Comparator<any>;
/**
 * @return {?}
 */
public get sortBy() {
        return this._sortBy;
    }
/**
 * @param {?} comparator
 * @return {?}
 */
public set sortBy(comparator: Comparator<any>|string) {
        if (typeof comparator === "string") {
            this._sortBy = new DatagridPropertyComparator(comparator);
        } else {
            if (comparator) {
                this._sortBy = comparator;
            } else {
                if (this._field) {
                    this._sortBy = new DatagridPropertyComparator(this._field);
                } else {
                    delete this._sortBy;
                }
            }
        }
    }
/**
 * Indicates if the column is sortable
 * @return {?}
 */
public get sortable(): boolean {
        return !!this._sortBy;
    }
/**
 * Indicates if the column is currently sorted
 * 
 * @deprecated This will be removed soon, in favor of the sortOrder mechanism
 */
private _sorted = false;
/**
 * @return {?}
 */
public get sorted() {
        return this._sorted;
    }
/**
 * @deprecated This will be removed soon, in favor of the sortOrder mechanism
 * @param {?} value
 * @return {?}
 */
public set sorted(value: boolean) {
        if (!value && this.sorted) {
            this._sorted = false;
            this._sort.clear();
        } else if (value && !this.sorted) {
            this.sort();
        }
    }
/**
 * @deprecated This will be removed soon, in favor of the sortOrder mechanism
 */
public sortedChange = new EventEmitter<boolean>();
/**
 * Indicates how the column is currently sorted
 */
private _sortOrder: SortOrder = SortOrder.Unsorted;
/**
 * @return {?}
 */
public get sortOrder() {
        return this._sortOrder;
    }
/**
 * @param {?} value
 * @return {?}
 */
public set sortOrder(value: SortOrder) {
        if (typeof value === "undefined") {
            return;
        }

        // only if the incoming order is different from the current one
        if (this._sortOrder === value) {
            return;
        }

        switch (value) {
            // the Unsorted case happens when the current state is either Asc or Desc
            default:
            case SortOrder.Unsorted:
                this._sort.clear();
                break;
            case SortOrder.Asc:
                this.sort(false);
                break;
            case SortOrder.Desc:
                this.sort(true);
                break;
        }
    }
public sortOrderChange = new EventEmitter<SortOrder>();
/**
 * Sorts the datagrid based on this column
 * @param {?=} reverse
 * @return {?}
 */
public sort(reverse?: boolean) {
        if (!this.sortable) {
            return;
        }

        this._sort.toggle(this._sortBy, reverse);

        // setting the private variable to not retrigger the setter logic
        this._sortOrder = this._sort.reverse ? SortOrder.Desc : SortOrder.Asc;
        this.sortOrderChange.emit(this._sortOrder);

        // deprecated: to be removed - START
        this._sorted = true;
        this.sortedChange.emit(true);
        // deprecated: to be removed - END
    }
/**
 * Indicates if the column is currently sorted in ascending order
 * @return {?}
 */
public get asc() {
        // deprecated: if condition to be removed - START
        if (typeof this.sortOrder === "undefined") {
            return this.sorted && !this._sort.reverse;
        } else {
            return this.sortOrder === SortOrder.Asc;
        }
        // deprecated: if condition to be removed - END
    }
/**
 * Indicates if the column is currently sorted in descending order
 * @return {?}
 */
public get desc() {
        // deprecated: if condition to be removed - START
        if (typeof this.sortOrder === "undefined") {
            return this.sorted && this._sort.reverse;
        } else {
            return this.sortOrder === SortOrder.Desc;
        }
        // deprecated: if condition to be removed - END
    }
/**
 * A custom filter for this column that can be provided in the projected content
 */
public customFilter = false;
/**
 * @param {?} custom
 * @return {?}
 */
public set projectedFilter(custom: any) {
        if (custom) {
            this.deleteFilter();
            this.customFilter = true;
        }
    }
/**
 * @return {?}
 */
public get filterValue() {
        return this.filter.value;
    }
/**
 * @param {?} newValue
 * @return {?}
 */
public set filterValue(newValue: string) {
        if (!this.filter) {
            return;
        }
        if (!newValue) {
            newValue = "";
        }
        if (newValue !== this.filter.value) {
            this.filter.value = newValue;
            this.filterValueChange.emit(newValue);
        }
    }

     filterValueChange = new EventEmitter();
/**
 * ********
 * 
 * \@property hideable
 * 
 * \@description 
 * When a column is hideable this is defined with an instance of DatagridHideableColumn.
 * When its not hideable should be undefined.
 * 
 */
public hideable: DatagridHideableColumn;
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
    selector: "clr-dg-column",
    template: `
        <div class="datagrid-column-flex">
            <!-- I'm really not happy with that select since it's not very scalable -->
            <ng-content select="clr-dg-filter, clr-dg-string-filter"></ng-content>

            <clr-dg-string-filter
                    *ngIf="field && !customFilter"
                    [clrDgStringFilter]="registered"
                    [(clrFilterValue)]="filterValue"></clr-dg-string-filter>

            <ng-template #columnTitle><ng-content></ng-content></ng-template>

            <button class="datagrid-column-title" *ngIf="sortable" (click)="sort()" type="button">
               <ng-container *ngTemplateOutlet="columnTitle"></ng-container>
            </button>

            <span class="datagrid-column-title" *ngIf="!sortable">
               <ng-container *ngTemplateOutlet="columnTitle"></ng-container>
            </span>

            <div class="datagrid-column-separator">
                <button #columnHandle class="datagrid-column-handle" tabindex="-1" type="button"></button>
                <div #columnHandleTracker class="datagrid-column-handle-tracker"></div>
            </div>
        </div>
    `,
    host: {"[class.datagrid-column]": "true", "[class.datagrid-column--hidden]": "hidden"}
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Sort, },
{type: FiltersProvider, },
{type: DragDispatcher, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'handleElRef': [{ type: ViewChild, args: ["columnHandle", ] },],
'handleTrackerElRef': [{ type: ViewChild, args: ["columnHandleTracker", ] },],
'field': [{ type: Input, args: ["clrDgField", ] },],
'sortBy': [{ type: Input, args: ["clrDgSortBy", ] },],
'sorted': [{ type: Input, args: ["clrDgSorted", ] },],
'sortedChange': [{ type: Output, args: ["clrDgSortedChange", ] },],
'sortOrder': [{ type: Input, args: ["clrDgSortOrder", ] },],
'sortOrderChange': [{ type: Output, args: ["clrDgSortOrderChange", ] },],
'asc': [{ type: HostBinding, args: ["class.asc", ] },],
'desc': [{ type: HostBinding, args: ["class.desc", ] },],
'projectedFilter': [{ type: ContentChild, args: [CustomFilter, ] },],
'filterValue': [{ type: Input, args: ["clrFilterValue", ] },],
'filterValueChange': [{ type: Output, args: ["clrFilterValueChange", ] },],
};
}

function DatagridColumn_tsickle_Closure_declarations() {
/** @type {?} */
DatagridColumn.decorators;
/**
 * @nocollapse
 * @type {?}
 */
DatagridColumn.ctorParameters;
/** @type {?} */
DatagridColumn.propDecorators;
/**
 * \@property columnId
 * 
 * \@description 
 * A DatagridColumn class variable that holds the number of DatagridColumn instances for a Datagrid.
 * It is used to generate a unique id for the DatagridColumn instance.
 * 
 * @type {?}
 */
DatagridColumn.prototype.columnId;
/**
 * Subscription to the sort service changes
 * @type {?}
 */
DatagridColumn.prototype._sortSubscription;
/** @type {?} */
DatagridColumn.prototype._field;
/**
 * Comparator to use when sorting the column
 * @type {?}
 */
DatagridColumn.prototype._sortBy;
/**
 * Indicates if the column is currently sorted
 * 
 * @deprecated This will be removed soon, in favor of the sortOrder mechanism
 * @type {?}
 */
DatagridColumn.prototype._sorted;
/**
 * @deprecated This will be removed soon, in favor of the sortOrder mechanism
 * @type {?}
 */
DatagridColumn.prototype.sortedChange;
/**
 * Indicates how the column is currently sorted
 * @type {?}
 */
DatagridColumn.prototype._sortOrder;
/** @type {?} */
DatagridColumn.prototype.sortOrderChange;
/**
 * A custom filter for this column that can be provided in the projected content
 * @type {?}
 */
DatagridColumn.prototype.customFilter;
/** @type {?} */
DatagridColumn.prototype.filterValueChange;
/**
 * ********
 * 
 * \@property hideable
 * 
 * \@description 
 * When a column is hideable this is defined with an instance of DatagridHideableColumn.
 * When its not hideable should be undefined.
 * 
 * @type {?}
 */
DatagridColumn.prototype.hideable;
/** @type {?} */
DatagridColumn.prototype._sort;
/** @type {?} */
DatagridColumn.prototype._dragDispatcher;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
